import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Mail, AlertTriangle, Copy, CheckCircle2 } from 'lucide-react';

// APEX AI Tool 2 — Coach DM & Email Personalizer.
// Pulls the student's own profile (GPA, IXL Mastery, PER, Hudl) server-side
// and drafts a Module-2-template email to a specific college coach.
export default function ApexCoachEmail() {
  const navigate = useNavigate();
  const [coachName, setCoachName] = useState('');
  const [programName, setProgramName] = useState('');
  const [positionTitle, setPositionTitle] = useState('');
  const [specificReason, setSpecificReason] = useState('');
  const [includeHudl, setIncludeHudl] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [profileUsed, setProfileUsed] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    setErr(''); setResult(null); setLoading(true);
    try {
      const r = await api.post('/apex/tools/coach-email', {
        coach_name: coachName.trim(),
        program_name: programName.trim(),
        position_coach_title: positionTitle.trim() || null,
        specific_reason: specificReason.trim() || null,
        include_hudl: includeHudl,
      });
      setResult(r.data.result);
      setProfileUsed(r.data.profile_used);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'AI service is unavailable — try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = async () => {
    if (!result?.email_body) return;
    const text = `Subject: ${result.subject_line}\n\n${result.email_body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="apex-coach-email-page">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button onClick={() => navigate('/apex/course')}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-2 mb-6"
                data-testid="apex-email-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to A.P.E.X.
        </button>

        <div className="flex items-start gap-3 mb-2">
          <Mail className="w-6 h-6 text-orange-400 mt-1" />
          <div>
            <div className="text-xs text-orange-500 uppercase tracking-widest mb-1">Module 2 · AI Tool</div>
            <h1 className="text-3xl font-black">Coach DM & Email Personalizer</h1>
          </div>
        </div>
        <p className="text-sm text-gray-400 mb-6">
          Coach B's AI pulls your real profile (GPA, IXL Mastery, PER analytics, Hudl) and drafts an outreach email using the exact APEX Module 2 template. Review, edit, then send.
        </p>

        <form onSubmit={run} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input placeholder="Coach's last name (e.g. Coach Johnson)" value={coachName}
                   onChange={(e) => setCoachName(e.target.value)} required
                   className="bg-black border-gray-700 text-white" data-testid="apex-email-coach-name" />
            <Input placeholder="Program (e.g. UCLA Basketball)" value={programName}
                   onChange={(e) => setProgramName(e.target.value)} required
                   className="bg-black border-gray-700 text-white" data-testid="apex-email-program" />
          </div>
          <Input placeholder="Position coach title (optional, e.g. Assistant Coach — Guards)"
                 value={positionTitle} onChange={(e) => setPositionTitle(e.target.value)}
                 className="bg-black border-gray-700 text-white" data-testid="apex-email-title" />
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-400 mb-1 block">
              Why this program? (specific reason — one line)
            </label>
            <Textarea value={specificReason} onChange={(e) => setSpecificReason(e.target.value)}
              placeholder={`e.g. "Coach McKinney's zone defense fits my instincts" or "returning senior at my position graduates 2027"`}
              className="bg-black border-gray-700 text-white min-h-[60px]" data-testid="apex-email-reason" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={includeHudl} onChange={(e) => setIncludeHudl(e.target.checked)}
                   data-testid="apex-email-include-hudl" />
            Include my Hudl film link in the email
          </label>

          {err && (
            <div className="flex items-start gap-2 p-3 rounded border border-red-500/40 bg-red-500/5"
                 data-testid="apex-email-error">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-300">{err}</p>
            </div>
          )}

          <Button type="submit" disabled={loading || !coachName.trim() || !programName.trim()}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold h-11"
                  data-testid="apex-email-run-btn">
            {loading ? 'Coach B is drafting…' : 'Draft My Outreach Email →'}
          </Button>
        </form>

        {result && (
          <div className="mt-8 space-y-4" data-testid="apex-email-result">
            {profileUsed && (
              <div className="text-[11px] text-gray-500 border border-gray-800 rounded p-3 bg-black/40">
                <div className="uppercase tracking-widest text-gray-400 mb-1">Profile data pulled from your account:</div>
                <div>GPA: <span className="text-white">{String(profileUsed.gpa)}</span> · Sport: <span className="text-white">{profileUsed.sport}</span> · Hudl: <span className="text-white">{profileUsed.hudl_url}</span></div>
              </div>
            )}

            <Card className="bg-[#111] border-orange-500/30">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-orange-400 uppercase tracking-widest font-bold">Draft Email</div>
                  <Button size="sm" variant="outline" onClick={copyEmail}
                          className="border-gray-700 text-white hover:bg-gray-800 text-xs h-7"
                          data-testid="apex-email-copy-btn">
                    {copied ? <><CheckCircle2 className="w-3 h-3 mr-1 text-green-400" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                  </Button>
                </div>
                <div className="mb-3">
                  <div className="text-[10px] text-gray-500 uppercase tracking-widest">Subject</div>
                  <div className="text-sm font-mono text-white" data-testid="apex-email-subject">{result.subject_line}</div>
                </div>
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-200 bg-black rounded border border-gray-800 p-3 leading-relaxed"
                     data-testid="apex-email-body">{result.email_body}</pre>
              </CardContent>
            </Card>

            {result.coaching_notes?.length > 0 && (
              <Card className="bg-[#111] border-amber-500/30">
                <CardContent className="p-5">
                  <div className="text-xs text-amber-400 uppercase tracking-widest font-bold mb-2">
                    Coach B's notes on your draft
                  </div>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {result.coaching_notes.map((n, i) => (
                      <li key={i} className="flex gap-2"><span className="text-amber-400">·</span>{n}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {result.confidence_flags && (
              <div className="text-[11px] text-gray-500 flex gap-4">
                <span>Real GPA: {result.confidence_flags.has_real_gpa ? '✓' : '—'}</span>
                <span>Real stats: {result.confidence_flags.has_real_stats ? '✓' : '—'}</span>
                <span>Specificity: {result.confidence_flags.specificity_score_out_of_10}/10</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
